import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Block } from "@alchimie/shared";
import { api } from "../api.js";
import { Button, COLORS, Empty } from "../ui.js";
import { BlocksWorkspace } from "../builder/BlocksWorkspace.js";

interface Lesson {
  id: string;
  moduleId: string;
  title: string;
  position: number;
  blocks: Block[];
  durationSec: number | null;
  module: {
    id: string;
    title: string;
    course: { id: string; title: string; slug: string };
  };
}

/**
 * Éditeur de Leçon — édite blocks d'une leçon. Pas de breakpoints overrides
 * pour la consommation (les leçons sont rendues dans le cadre de la sidebar
 * Client, le responsive y est géré différemment).
 */
export default function LessonEditor() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const nav = useNavigate();

  const { data: lesson, isLoading } = useQuery({
    queryKey: ["lesson", id],
    queryFn: () => api.get<Lesson>(`/lessons/${id}`),
    enabled: !!id,
  });

  const [blocks, setBlocks] = useState<Block[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (lesson) {
      setBlocks(lesson.blocks ?? []);
      setDirty(false);
    }
  }, [lesson?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (next: { blocks?: Block[]; title?: string }) =>
      api.patch<Lesson>(`/lessons/${id}`, next),
    onSuccess: (data) => {
      qc.setQueryData(["lesson", id], data);
      qc.invalidateQueries({ queryKey: ["course", lesson?.module.course.id] });
      setDirty(false);
    },
  });

  if (isLoading) return <Empty>Chargement…</Empty>;
  if (!lesson) return <Empty>Leçon introuvable.</Empty>;

  return (
    <BlocksWorkspace
      blocks={blocks}
      onBlocksChange={(b) => {
        setBlocks(b);
        setDirty(true);
      }}
      overrides={{}}
      onOverridesChange={() => {}}
      onPersist={() => save.mutate({ blocks })}
      // Pour une leçon, on autorise tous les blocs sauf PRICING (spécifique
      // aux pages de vente). CTA reste utile pour rediriger.
      topbarLeft={
        <button
          onClick={() => nav(`/courses/${lesson.module.course.id}`)}
          style={{
            border: `1px solid ${COLORS.border}`,
            background: "transparent",
            padding: "6px 12px",
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "inherit",
          }}
        >
          ← {lesson.module.course.title}
        </button>
      }
      topbarTitle={
        <>
          <span style={{ color: COLORS.muted, fontWeight: 400 }}>{lesson.module.title} /</span> {lesson.title}
        </>
      }
      topbarRight={
        <>
          <div style={{ fontSize: 12, color: COLORS.muted, marginLeft: 8 }}>
            {dirty ? "Non enregistré" : "À jour"}
          </div>
          <Button onClick={() => save.mutate({ blocks })} disabled={!dirty || save.isPending}>
            {save.isPending ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </>
      }
    />
  );
}
